import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  getClientPortalUsers,
  inviteClientPortalUser,
  revokeClientPortalUser,
} from "../../../lib/api";
import type { ClientRecord } from "../types";
import type { PortalUserLink } from "../../portal/types";
import { Modal } from "../../../components/Modal";
import styles from "./PortalUsersModal.module.css";

export interface PortalUsersModalProps {
  client: ClientRecord;
  onClose: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Manage portal access for one client (Phase B, US-CDD-005): invite a linked
 * user (email + display name + initial password), revoke access, and see who
 * is linked. Account creation/reuse happens server-side; this modal only
 * collects credentials and renders the link list.
 *
 * Rendered through the shared {@link Modal} so the scrim, panel, header rule,
 * Escape handling, scroll lock and focus trap are the app's, not this file's.
 */
export function PortalUsersModal({ client, onClose }: PortalUsersModalProps) {
  const [users, setUsers] = useState<PortalUserLink[] | null>(null);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const body = await getClientPortalUsers(client.id);
      setUsers(body.portalUsers);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load portal users");
    }
  }, [client.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleInvite = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    const trimmedEmail = email.trim();
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setError("Enter a valid email address.");
      return;
    }
    if (password.length < 8) {
      setError("Initial password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await inviteClientPortalUser(client.id, {
        email: trimmedEmail,
        displayName: displayName.trim(),
        password,
      });
      setNotice(
        result.accountCreated
          ? `Portal account created for ${trimmedEmail}. Share the initial password securely.`
          : `Existing account ${trimmedEmail} linked to ${client.name}.`,
      );
      setEmail("");
      setDisplayName("");
      setPassword("");
      await load();
    } catch (inviteError) {
      setError(inviteError instanceof Error ? inviteError.message : "Invite failed");
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (user: PortalUserLink): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await revokeClientPortalUser(client.id, user.authUserId);
      await load();
    } catch (revokeError) {
      setError(revokeError instanceof Error ? revokeError.message : "Revoke failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Portal access"
      subtitle={client.name}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={styles.close} onClick={onClose}>
            Close
          </button>
          <button type="submit" form="portalInviteForm" className={styles.invite} disabled={busy}>
            {busy ? "Working…" : "Invite portal user"}
          </button>
        </>
      }
    >
      <div className={styles.body}>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        {notice && (
          <p className={styles.notice} role="status">
            {notice}
          </p>
        )}

        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Linked accounts</h3>
          {users === null ? (
            <p className={styles.note}>Loading…</p>
          ) : users.length === 0 ? (
            <p className={styles.note}>No portal users yet.</p>
          ) : (
            <ul className={styles.list}>
              {users.map((user) => (
                <li key={user.authUserId} className={styles.row}>
                  <span className={styles.userCell}>
                    <span className={styles.userEmail}>{user.email}</span>
                    <span className={styles.userMeta}>{user.displayName || "—"}</span>
                  </span>
                  {user.revokedAt ? (
                    <span className={styles.badgeRevoked}>revoked</span>
                  ) : (
                    <button
                      type="button"
                      className={styles.revoke}
                      disabled={busy}
                      onClick={() => void handleRevoke(user)}
                    >
                      Revoke
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <form
          id="portalInviteForm"
          className={styles.section}
          onSubmit={(event) => void handleInvite(event)}
        >
          <h3 className={styles.sectionTitle}>Invite someone new</h3>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span className={styles.label}>Email</span>
              <input
                className={styles.input}
                type="email"
                name="portalEmail"
                value={email}
                autoComplete="off"
                required
                onChange={(event) => setEmail(event.currentTarget.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Display name</span>
              <input
                className={styles.input}
                name="portalDisplayName"
                value={displayName}
                autoComplete="off"
                onChange={(event) => setDisplayName(event.currentTarget.value)}
              />
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Initial password</span>
              <input
                className={styles.input}
                type="password"
                name="portalPassword"
                value={password}
                autoComplete="new-password"
                minLength={8}
                required
                onChange={(event) => setPassword(event.currentTarget.value)}
              />
            </label>
          </div>
          <p className={styles.hint}>
            Minimum 8 characters. Share the initial password through a channel the recipient already
            trusts.
          </p>
        </form>
      </div>
    </Modal>
  );
}
